<template>
  <div>
    <div class="card card-default card-shadow">
      <div class="card-header">{{ translate('settings.availability.title') }}</div>

      <div class="card-body">
        <div class="alert alert-success mt-0" v-if="updateSettingsAvailabilityForm.successful">
          <i class="fas fa-check-circle"></i> {{ translate('settings.availability.success') }}
        </div>

        <div class="form-group row">
          <div class="form-check col-md-3 text-md-right">
            <label class="form-check-label form-check-toggle">
              <input class="form-check-input"
                     type="checkbox"
                     name="availability"
                     v-model="alwaysAvailable"
                     @click="resetAvailableFields" /><span></span>
            </label>
          </div>
          <label class="col-md-7 col-form-label text-md-left">{{ translate('settings.availability.form.always_available') }}</label>
        </div>

        <div class="form-group row" v-if="!alwaysAvailable">
          <label class="col-md-3 col-form-label text-md-right">{{ translate('settings.availability.form.available_from') }}</label>

          <div class="col-md-7">
            <datepicker
              v-model="updateSettingsAvailabilityForm.available_from"
              name="available_from"
              :input-class="{'is-invalid': updateSettingsAvailabilityForm.errors.has('available_from')}"
              :bootstrap-styling="true">
            </datepicker>
            <span class="invalid-feedback"
                  v-if="updateSettingsAvailabilityForm.errors.has('available_from')">
                        {{ updateSettingsAvailabilityForm.errors.get('available_from') }}
            </span>
          </div>
        </div>

        <div class="form-group row" v-if="!alwaysAvailable">
          <label class="col-md-3 col-form-label text-md-right">{{ translate('settings.availability.form.available_to') }}</label>

          <div class="col-md-7">
            <datepicker
              v-model="updateSettingsAvailabilityForm.available_to"
              name="available_to"
              :input-class="{'is-invalid': updateSettingsAvailabilityForm.errors.has('available_to')}"
              :bootstrap-styling="true">
            </datepicker>
            <span class="invalid-feedback" v-if="updateSettingsAvailabilityForm.errors.has('available_to')">
                        {{ updateSettingsAvailabilityForm.errors.get('available_to') }}
                    </span>
          </div>
        </div>

        <div class="form-group row">
          <label class="col-md-3 col-form-label text-md-right">{{ translate('settings.availability.form.set_hours') }}</label>

          <div class="col-md-7">
            <div class="form-group row mb-0">
              <div class="col-md-3 offset-2 text-center">
                {{ translate('settings.availability.from') }}
              </div>
              <div class="col-md-3 text-center">
                {{ translate('settings.availability.to') }}
              </div>
              <div class="col-md-3 text-center">
                {{ translate('settings.availability.available_slots') }}
              </div>
            </div>

            <div class="form-group row" v-for="i in [0, 1, 2, 3, 4, 5, 6]">
              <label class="col-md-1 col-form-label text-md-right">
                {{ translate('settings.availability.form.' + weekdays[i]) }}
              </label>
              <div class="form-check col-md-1">
                <label class="form-check-label form-check-toggle">
                  <input class="form-check-input"
                         type="checkbox"
                         checked="checked"
                         v-model="support['day_' + i + '_checkbox']"
                         @click="toggleDayActive(i)" />
                  <span></span>
                </label>
              </div>
              <div class="col-md-3">
                <vue-timepicker
                  close-on-complete
                  hide-clear-button
                  :name="'day_' + i + '_start'"
                  :minute-interval="60"
                  v-if="support['day_' + i + '_checkbox']"
                  v-model="updateSettingsAvailabilityForm['day_' + i].start"
                  :class="{'is-invalid': updateSettingsAvailabilityForm.errors.has('day_' + i + '.start')}"
                  placeholder="start"
                  format="HH:mm">
                </vue-timepicker>
                <span class="invalid-feedback"
                      v-if="updateSettingsAvailabilityForm.errors.has('day_' + i + '.start')"
                      v-text="updateSettingsAvailabilityForm.errors.get('day_' + i + '.start')">
                                </span>
              </div>
              <div class="col-md-3">
                <vue-timepicker
                  close-on-complete
                  hide-clear-button
                  :name="'day_' + i + '_end'"
                  :minute-interval="60"
                  v-if="support['day_' + i + '_checkbox']"
                  v-model="updateSettingsAvailabilityForm['day_' + i].end"
                  :class="{'is-invalid': updateSettingsAvailabilityForm.errors.has('day_' + i + '.end')}"
                  placeholder="end"
                  format="HH:mm">
                </vue-timepicker>
                <span class="invalid-feedback"
                      v-if="updateSettingsAvailabilityForm.errors.has('day_' + i + '.end')"
                      v-text="updateSettingsAvailabilityForm.errors.get('day_' + i + '.end')">
                                </span>
              </div>
              <div class="col-md-3">
                <input type="text"
                       class="form-control"
                       :name="'day_' + i + '_slots'"
                       v-if="support['day_' + i + '_checkbox']"
                       v-model="updateSettingsAvailabilityForm['day_' + i].slots"
                       :class="{'is-invalid': updateSettingsAvailabilityForm.errors.has('day_' + i + '.slots')}"
                       placeholder="slots">
                <span class="invalid-feedback"
                      v-if="updateSettingsAvailabilityForm.errors.has('day_' + i + '.slots')"
                      v-text="updateSettingsAvailabilityForm.errors.get('day_' + i + '.slots')">
                            </span>
              </div>
            </div>
          </div>
        </div>

        <div class="form-group row">
          <label class="col-md-3 col-form-label text-md-right mt-3">
            {{ translate('settings.availability.form.break') }}
          </label>

          <div class="col-md-7">
            <div class="form-group row mb-0">
              <div class="col-md-3 text-center">
                {{ translate('settings.availability.from') }}
              </div>
              <div class="col-md-3 text-center">
                {{ translate('settings.availability.to') }}
              </div>
            </div>
            <div class="form-group row">
              <div class="col-md-3">
                <vue-timepicker
                  close-on-complete
                  hide-clear-button
                  name="break_start"
                  :minute-interval="60"
                  v-model="updateSettingsAvailabilityForm.break_start"
                  :class="{'is-invalid': updateSettingsAvailabilityForm.errors.has('break_start')}"
                  placeholder="start"
                  format="HH:mm">
                </vue-timepicker>
                <span class="invalid-feedback"
                      v-if="updateSettingsAvailabilityForm.errors.has('break_start')"
                      v-text="updateSettingsAvailabilityForm.errors.get('break_start')">
                                </span>
              </div>
              <div class="col-md-3">
                <vue-timepicker
                  close-on-complete
                  hide-clear-button
                  name="break_end"
                  :minute-interval="60"
                  v-model="updateSettingsAvailabilityForm.break_end"
                  :class="{'is-invalid': updateSettingsAvailabilityForm.errors.has('break_end')}"
                  placeholder="end"
                  format="HH:mm">
                </vue-timepicker>
                <span class="invalid-feedback"
                      v-if="updateSettingsAvailabilityForm.errors.has('break_end')"
                      v-text="updateSettingsAvailabilityForm.errors.get('break_end')">
                                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="form-group row">
          <label for="min_booking_notice" class="col-md-3 col-form-label text-md-right">
            {{ translate('settings.availability.form.min_booking_notice') }}
          </label>

          <div class="col-md-7">
            <input id="min_booking_notice"
                   type="text"
                   class="form-control"
                   name="min_booking_notice"
                   v-model.number="updateSettingsAvailabilityForm.min_booking_notice"
                   max="10"
                   min="0"
                   :class="{'is-invalid': updateSettingsAvailabilityForm.errors.has('min_booking_notice')}"
                   required autofocus>

            <span class="invalid-feedback"
                  role="alert"
                  v-if="updateSettingsAvailabilityForm.errors.has('min_booking_notice')">
                            <strong>{{ updateSettingsDatesForm.errors.get('min_booking_notice') }}</strong>
                        </span>
          </div>
        </div>

        <div class="form-group row mb-0">
          <div class="offset-md-4 col-md-6">
            <button type="submit"
                    class="btn btn-primary"
                    @click.prevent="updateSettingsAvailability"
                    :disabled="updateSettingsAvailabilityForm.busy">
              {{ translate('settings.availability.form.update') }}
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker';
import VueTimepicker from 'vue2-timepicker/src/vue-timepicker.vue';
import moment from 'moment';

export default {
  props: ['setting'],

  components: {
    Datepicker,
    VueTimepicker
  },

  /**
   * The component's data.
   */
  data () {
    return {
      updateSettingsAvailabilityForm: new AppForm({
        break_start: null,
        break_end: null,

        available_from: null,
        available_to: null,
        min_booking_notice: null,

        day_0: { start: null, end: null, slots: null },
        day_1: { start: null, end: null, slots: null },
        day_2: { start: null, end: null, slots: null },
        day_3: { start: null, end: null, slots: null },
        day_4: { start: null, end: null, slots: null },
        day_5: { start: null, end: null, slots: null },
        day_6: { start: null, end: null, slots: null },
      }),

      support: {
        day_0_checkbox: false,
        day_1_checkbox: false,
        day_2_checkbox: false,
        day_3_checkbox: false,
        day_4_checkbox: false,
        day_5_checkbox: false,
        day_6_checkbox: false,
      },

      weekdays: {
        0: 'mon',
        1: 'tue',
        2: 'wed',
        3: 'thu',
        4: 'fri',
        5: 'sat',
        6: 'sun',
      },

      alwaysAvailable: true,
    };
  },

  /**
   * Prepare the component.
   */
  mounted () {
    let weekdays = JSON.parse(this.setting.weekdays);
    this.updateSettingsAvailabilityForm.available_from = this.setting.available_from;
    this.updateSettingsAvailabilityForm.available_to = this.setting.available_to;

    this.updateSettingsAvailabilityForm.break_start = this.setting.break_start;
    this.updateSettingsAvailabilityForm.break_end = this.setting.break_end;
    this.updateSettingsAvailabilityForm.min_booking_notice = this.setting.min_booking_notice;

    this.alwaysAvailable = !this.setting.available_from || !this.setting.available_to;

    for (let i = 0; i < 7; i++) {
      this.support['day_' + i + '_checkbox'] = 'day_' + i in weekdays;

      if ('day_' + i in weekdays) {
        this.updateSettingsAvailabilityForm['day_' + i] = weekdays['day_' + i];
      }
    }
  },

  methods: {
    /**
     * Update the Option Availability.
     */
    updateSettingsAvailability () {
      for (let i = 0; i < 7; i++) {
        if (this.support['day_' + i + '_checkbox']) {

          if (this.updateSettingsAvailabilityForm['day_' + i].start && typeof this.updateSettingsAvailabilityForm['day_' + i].start === 'object') {
            this.updateSettingsAvailabilityForm['day_' + i].start = moment()
              .hour(this.updateSettingsAvailabilityForm['day_' + i].start.HH)
              .minute(0)
              .format('HH:mm');
          }

          if (this.updateSettingsAvailabilityForm['day_' + i].end && typeof this.updateSettingsAvailabilityForm['day_' + i].end === 'object') {
            this.updateSettingsAvailabilityForm['day_' + i].end = moment()
              .hour(this.updateSettingsAvailabilityForm['day_' + i].end.HH)
              .minute(0)
              .format('HH:mm');
          }

        }
      }

      App.put('/api/settings/1/availability', this.updateSettingsAvailabilityForm)
        .then(response => {
          this.$parent.$emit('updateSettings');
        });
    },

    resetAvailableFields () {
      this.updateSettingsAvailabilityForm.available_from = null;
      this.updateSettingsAvailabilityForm.available_to = null;
    },

    toggleDayActive (i) {
      this.support['day_' + i + '_checkbox'] = !this.support['day_' + i + '_checkbox'];
      this.updateSettingsAvailabilityForm['day_' + i] =
        this.support['day_' + i + '_checkbox'] ? this.updateSettingsAvailabilityForm['day_' + i] : {
          start: null,
          end: null,
          slots: null
        };
    },
  }
};
</script>

<style>
.vue__time-picker input.display-time {
    display: block !important;
    width: 100% !important;
    height: calc(1.6em + 0.75rem + 2px) !important;
    padding: 0.375rem 0.75rem !important;
    font-size: 0.9rem !important;
    font-weight: 400 !important;
    line-height: 1.6 !important;
    color: #495057 !important;
    background-color: #fff !important;
    background-clip: padding-box !important;
    border: 1px solid #ced4da !important;
    border-radius: 0.25rem !important;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
}

.form-check {
    margin-bottom: 0;
}
</style>
